import { Box } from '@mui/material';
import { Header } from 'components/home';
import { StepperHorizontal } from 'components/shared/stepper_horizontal';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Step1 from './Step1';
import Step2 from './Step2';
import useCifaStore from 'stores/cifa/cifa.store';
import { ROUTES } from 'routes/constants';
import { CifaStatusEnum } from 'enum/cifaStatus.enum';
import CifaAPI from 'api/cifa/cifa.api';

export interface FinancialResourceForm {
  tipo: 'Programa' | 'Projeto' | 'Ação/Atividade';
  valor_orcado: number;
  descricao: string;
  valor_liquidado: number;
}

interface StepData {
  recursos: FinancialResourceForm[];
  rcl: number;
}

export const parseSafeNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const FinancialResources: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = RegExp(/cifa-(\d{4})/).exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  const steps = ['Cadastro de Recursos', 'Confirmação dos Dados'];
  const [activeStep, setActiveStep] = useState<number>(0);
  const { cifa, setPartialForm } = useCifaStore();
  const [localData, setLocalData] = useState<StepData>({
    recursos: [],
    rcl: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });



  useEffect(() => {
    if (!cifa.uuid) {
      navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
      return;
    }

    setLocalData({
      recursos: cifa.recursos_financeiros_aplicados_detalhamento || [],
      rcl: parseSafeNumber(cifa.receita_corrente_liquida_2025)
    });
  }, [cifa, navigate, anoReferencia]);


  const handleNext = (data: StepData) => {
    setLocalData(data);
    setPartialForm('recursos_financeiros_aplicados_detalhamento', data.recursos);
    setPartialForm('receita_corrente_liquida_2025', data.rcl);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Garantir que todos os dados estejam atualizados no store
      const currentData = useCifaStore.getState().cifa;

      await CifaAPI.updateFinancialResource(cifa.uuid!, {
        recursos_financeiros_aplicados_detalhamento: localData.recursos,
        receita_corrente_liquida_2025: localData.rcl,
        status_aba_4: CifaStatusEnum.APPROVED
      });


      setSnackbar({
        open: true,
        message: 'Dados salvos com sucesso!',
        severity: 'success'
      });

      setTimeout(() => navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string), 1500);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar os dados. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title={`CIFA - Recursos Financeiros - ${anoReferencia}`} showBackButton={true} />

      <Box display='flex' width='97%' justifyContent='center' flexDirection='column' alignItems='center' margin='2rem'>
        <StepperHorizontal activeStep={activeStep} steps={steps} maxWidth='1152px' />

        {activeStep === 0 && (
          <Step1
            initialData={localData}
            onNext={handleNext}
            loading={isSubmitting}
          />
        )}

        {activeStep === 1 && (
          <Step2
            data={localData}
            onBack={handleBack}
            onConfirm={handleSubmit}
            loading={isSubmitting}
          />
        )}
      </Box>
    </MenuProvider>
  );
};

export default FinancialResources;
